import  React, { useState, useEffect, useRef  } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icons } from "../../assest/icons";
import { toast } from "react-toastify";
import LeftSideLogin from "../leftsidelogin";
import { logger } from '../../utils/logger';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../../firebase.config';
import { fetchWithAppCheck } from '../../utils/fetchWithAppCheck';
import Modal from 'react-modal';
import ConsentNotice from '../consenttext';


function AdminLoginScreen() {
  const navigate = useNavigate();

  // If already logged in, redirect to admin dashboard
  useEffect(() => {
    const adminProfile = localStorage.getItem('UserProfileAdmin');
    const adminLoginStatus = localStorage.getItem('UserLoginStatusAdmin');
    if (adminProfile && adminLoginStatus === 'true') {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [navigate]);

  // ── Step 1: email + password ──
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [ShowLoader, setShowLoader] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [passwordType, setPasswordType] = useState("password");
  const [showConsentModal, setShowConsentModal] = useState(false);
  const openConsentModal = () => setShowConsentModal(true);
  const closeConsentModal = () => setShowConsentModal(false);

  // ── Step 2: 2FA OTP ──
  const [otpStep, setOtpStep] = useState(false);
  const [pendingAdminId, setPendingAdminId] = useState('');
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)];
  const otp = otpDigits.join('');

  const handleClickPassword = () => {
    setPasswordType(prev => prev === "password" ? "text" : "password");
  };
  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    setErrors('');
  };
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setErrors('');
  };

  useEffect(() => {
    const storedRememberMe = localStorage.getItem('rememberMe');
    if (storedRememberMe) {
      setRememberMe(JSON.parse(storedRememberMe));
    }
  }, []);

  // Focus first OTP input when OTP step becomes active
  useEffect(() => {
    if (otpStep && otpRefs[0].current) {
      otpRefs[0].current.focus();
    }
  }, [otpStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOtpDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...otpDigits];
    newDigits[index] = value.slice(-1);
    setOtpDigits(newDigits);
    setOtpError('');
    if (value && index < 5) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const newDigits = [...otpDigits];
    for (let i = 0; i < 6; i++) {
      newDigits[i] = pasted[i] || '';
    }
    setOtpDigits(newDigits);
    const focusIdx = Math.min(pasted.length, 5);
    otpRefs[focusIdx].current?.focus();
  };

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Start cooldown when OTP step activates
  useEffect(() => {
    if (otpStep) setResendCooldown(30);
  }, [otpStep]);

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    setOtpError('');
    setOtpDigits(['', '', '', '', '', '']);

    try {
      const functionsBase = process.env.REACT_APP_FUNCTIONS_BASE_URL
        || 'https://us-central1-aigicare-prod.cloudfunctions.net';

      const res = await fetchWithAppCheck(`${functionsBase}/adminLogin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const data = await res.json();

      if (res.ok && data.status === 'otp_required') {
        setPendingAdminId(data.adminId);
        setResendCooldown(30);
        toast.success('New verification code sent to your email.', {
          position: toast.POSITION.TOP_RIGHT,
        });
        otpRefs[0].current?.focus();
      } else {
        setOtpError(data.message || 'Failed to resend code. Please try again.');
      }
    } catch (error) {
      logger.error('Error resending OTP:', error);
      setOtpError('Failed to resend code. Please try again.');
    }
  };

  const handleResetForm = () => {
    setEmail('');
    setPassword('');
    setErrors('');
  };
  const nav = useNavigate();

  // ── Step 1 submit: verify email + password, request OTP ──
  const handleSubmit = async (event) => {
    event.preventDefault();

    let formErrors = {};
    if (!email.trim()) formErrors.email = 'Please enter your email';
    if (!password.trim()) formErrors.password = 'Please enter your password';
    if (!isValidEmail(email)) formErrors.email = 'Please enter a valid email address';

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setShowLoader(true);

    try {
      const functionsBase = process.env.REACT_APP_FUNCTIONS_BASE_URL
        || 'https://us-central1-aigicare-prod.cloudfunctions.net';

      const loginRes = await fetchWithAppCheck(`${functionsBase}/adminLogin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const loginData = await loginRes.json();

      if (!loginRes.ok) {
        toast.error(loginData.message || 'Invalid email or password', {
          position: toast.POSITION.TOP_RIGHT,
        });
        setShowLoader(false);
        return;
      }

      // adminLogin now returns { status: "otp_required", adminId }
      if (loginData.status === 'otp_required') {
        setPendingAdminId(loginData.adminId);
        setOtpDigits(['', '', '', '', '', '']);
        setOtpError('');
        setShowLoader(false);
        setOtpStep(true);
        toast.info('A 6-digit verification code has been sent to your email.', {
          position: toast.POSITION.TOP_RIGHT,
        });
        return;
      }

      // Fallback: if server returns customToken directly (should not happen after 2FA deploy)
      if (loginData.customToken) {
        await _finalizeLogin(loginData.customToken, loginData.adminData);
      }

    } catch (error) {
      logger.error('Error:', error);
      toast.error(error.message || 'An error occurred. Please try again.', {
        position: toast.POSITION.TOP_RIGHT,
      });
      setShowLoader(false);
    }
  };

  // ── Step 2 submit: verify OTP, receive custom token ──
  const handleOTPSubmit = async (event) => {
    event.preventDefault();

    if (!otp.trim() || otp.trim().length !== 6) {
      setOtpError('Please enter the 6-digit code sent to your email.');
      return;
    }

    setOtpLoading(true);
    setOtpError('');

    try {
      const functionsBase = process.env.REACT_APP_FUNCTIONS_BASE_URL
        || 'https://us-central1-aigicare-prod.cloudfunctions.net';

      const otpRes = await fetchWithAppCheck(`${functionsBase}/verifyAdminOTP`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminId: pendingAdminId, otp: otp.trim() }),
      });
      const otpData = await otpRes.json();

      if (!otpRes.ok) {
        setOtpError(otpData.message || 'Invalid verification code.');
        // If too many attempts or expired, return to login step
        if (otpRes.status === 429 || otpData.message?.includes('expired') || otpData.message?.includes('log in again')) {
          setOtpStep(false);
          setPendingAdminId('');
          setOtpDigits(['', '', '', '', '', '']);
          handleResetForm();
        }
        setOtpLoading(false);
        return;
      }

      await _finalizeLogin(otpData.customToken, otpData.adminData);

    } catch (error) {
      logger.error('Error verifying OTP:', error);
      setOtpError(error.message || 'Verification failed. Please try again.');
      setOtpLoading(false);
    }
  };

  // Shared finalization: sign in to Firebase, save to localStorage, redirect
  const _finalizeLogin = async (customToken, adminWithRole) => {
    await signInWithCustomToken(auth, customToken);

    toast.success('Admin signin successfully!', {
      position: toast.POSITION.TOP_RIGHT,
    });

    localStorage.setItem('UserIdAdmin', JSON.stringify(adminWithRole.id));
    localStorage.setItem('UserLoginStatusAdmin', JSON.stringify(true));
    localStorage.setItem('AdminRole', adminWithRole.role || 'admin');

    if (rememberMe) {
      localStorage.setItem('rememberMe', JSON.stringify(true));
    } else {
      localStorage.removeItem('rememberMe');
    }

    localStorage.setItem('UserProfileAdmin', JSON.stringify(adminWithRole));
    sessionStorage.setItem('aigi_session_alive', 'true');

    setShowLoader(false);
    setOtpLoading(false);
    handleResetForm();
    nav("/admin/dashboard", { state: adminWithRole, replace: true });
  };

  const handleBackToLogin = () => {
    setOtpStep(false);
    setPendingAdminId('');
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');
    setPassword('');
    setErrors('');
  };

  const isValidEmail = (emailVal) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(emailVal);
  };

  return (
    <div>
      <div className='container-fluid p-0 bgImageUrl'>

        <div className='container p-0 contentBody loginScreen'>

          <LeftSideLogin />

          <div className="col-lg-5 d-inline-block  position-relative">
            <div className="loginBoxDiv">

              {/* ── STEP 1: Email + Password ── */}
              {!otpStep && (
                <>
                  <h3 className="titleDespTxt">Admin Sign in</h3>
                  <form onSubmit={handleSubmit} autoComplete="on">

                    <div className='inputGroupDiv'>
                      <div className="inputTxtBox registerInputDiv">
                        <img src={Icons[0].mailImg} className="logoImg" alt="" />
                        <input type="email" value={email} placeholder="Email id" autoComplete="email" onChange={handleEmailChange} />
                      </div>
                      {errors.email && <span className="errorTxt">{errors.email}</span>}
                    </div>

                    <div className='inputGroupDiv'>
                      <div className="inputTxtBox registerInputDiv">
                        <img src={Icons[0].lockedImg} className="logoImg" alt="" />
                        <input type={passwordType} value={password} placeholder="Password" autoComplete="current-password" onChange={handlePasswordChange} />
                        <label className='eyePwdIcon' onClick={handleClickPassword}>
                          <img src={passwordType === "password" ? Icons[0].closeEye : Icons[0].opneEye} className="logoImg" alt="" />
                        </label>
                      </div>
                      {errors.password && <span className="errorTxt">{errors.password}</span>}
                    </div>

                    {ShowLoader ? (
                      <button className="signInBtn">Loading
                        <div className="spinner-border text-light" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </button>
                    ) : (
                      <button className="signInBtn" type="submit">Sign In</button>
                    )}

                    <div className="signupDiv">
                      <Link to="/admin/forgotpassword">Forgot Password?</Link>
                    </div>

                    {/* Privacy Policy & Terms */}
                    <div style={{ marginTop: '14px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
                      By signing in, you agree to our{' '}
                      <span
                        style={{ color: '#0d6efd', cursor: 'pointer', fontWeight: '600' }}
                        onClick={() => openConsentModal()}
                      >
                        Privacy Policy &amp; Terms
                      </span>
                    </div>

                  </form>
                </>
              )}

              {/* ── STEP 2: 2FA OTP Verification ── */}
              {otpStep && (
                <>
                  <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                    <div style={{
                      width: '56px', height: '56px', borderRadius: '50%',
                      background: 'linear-gradient(135deg, #024C9C 0%, #0370e0 100%)',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: '12px'
                    }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    </div>
                    <h3 className="titleDespTxt" style={{ marginBottom: '6px' }}>Verification Code</h3>
                    <p style={{ color: '#666', fontSize: '13px', margin: '0 0 24px', lineHeight: '1.5' }}>
                      We sent a 6-digit code to<br/><strong style={{ color: '#024C9C' }}>{email}</strong>
                    </p>
                  </div>
                  <form onSubmit={handleOTPSubmit} autoComplete="off">

                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '8px' }}>
                      {otpDigits.map((digit, idx) => (
                        <input
                          key={idx}
                          ref={otpRefs[idx]}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          autoComplete="one-time-code"
                          onChange={(e) => handleOtpDigitChange(idx, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                          onPaste={idx === 0 ? handleOtpPaste : undefined}
                          style={{
                            width: '48px', height: '56px',
                            border: `2px solid ${otpError ? '#dc3545' : digit ? '#024C9C' : '#d0d5dd'}`,
                            borderRadius: '12px',
                            fontSize: '22px', fontWeight: '700',
                            textAlign: 'center', color: '#1a1a1a',
                            outline: 'none',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                            background: digit ? '#f0f6ff' : '#fff',
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#024C9C';
                            e.target.style.boxShadow = '0 0 0 3px rgba(2,76,156,0.15)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = digit ? '#024C9C' : '#d0d5dd';
                            e.target.style.boxShadow = 'none';
                          }}
                        />
                      ))}
                    </div>
                    {otpError && (
                      <p style={{ color: '#dc3545', fontSize: '13px', textAlign: 'center', margin: '4px 0 0' }}>{otpError}</p>
                    )}

                    <p style={{ fontSize: '13px', color: '#666', textAlign: 'center', margin: '16px 0 0' }}>
                      Didn't receive the code?{' '}
                      {resendCooldown > 0 ? (
                        <span style={{ color: '#999' }}>Resend in {resendCooldown}s</span>
                      ) : (
                        <span
                          style={{ color: '#024C9C', cursor: 'pointer', fontWeight: '600' }}
                          onClick={handleResendOTP}
                        >
                          Resend Code
                        </span>
                      )}
                    </p>

                    {otpLoading ? (
                      <button className="signInBtn" style={{ marginTop: '24px' }}>Verifying
                        <div className="spinner-border text-light" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </button>
                    ) : (
                      <button className="signInBtn" type="submit" style={{ marginTop: '24px' }}>Verify &amp; Sign In</button>
                    )}

                    <div style={{ marginTop: '12px', textAlign: 'center' }}>
                      <span
                        style={{ color: '#0d6efd', cursor: 'pointer', fontSize: '13px' }}
                        onClick={handleBackToLogin}
                      >
                        &larr; Back to login
                      </span>
                    </div>

                  </form>
                </>
              )}

            </div>
          </div>

        </div>

      </div>

      {/* Consent / Privacy Policy Modal */}
      <Modal
        className="consentCheckModal modalViewAllMain"
        isOpen={showConsentModal}
        onRequestClose={closeConsentModal}
        contentLabel="Privacy Policy & Terms"
      >
        <div className="innerModalDiv overflow-hidden">
          <div style={{ padding: '10px 16px 0', borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h5 style={{ margin: 0, fontWeight: '700', color: '#024C9C' }}>Privacy Policy &amp; Terms</h5>
            <span onClick={closeConsentModal} style={{ cursor: 'pointer', fontSize: '22px', lineHeight: 1, color: '#666' }}>&times;</span>
          </div>
          <ConsentNotice onAgree={closeConsentModal} onDecline={closeConsentModal} />
        </div>
      </Modal>

    </div>
  );
}

export default AdminLoginScreen;
